import type { ReactNode } from 'react';
import { Slider, Tooltip } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';

import { Button } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { BAC_WEB, CAP_VAN, CHON_BIEU_DO, chuanHoa, DO_DAI, type BacWeb } from '../../nghiep-vu/hoi';

function Nhom({ ten, ghi, children }: { ten: string; ghi?: string; children: ReactNode }) {
  return (
    <div className="px-1 py-2">
      <p className="px-2.5 text-xs font-medium uppercase tracking-wide text-content-secondary">{ten}</p>
      {ghi && <p className="px-2.5 pt-0.5 text-xs leading-snug text-content-muted">{ghi}</p>}
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Muc({ nhan, mo, chon, onChon }: { nhan: string; mo: string; chon: boolean; onChon: () => void }) {
  return (
    <button
      type="button"
      onClick={onChon}
      aria-pressed={chon}
      className={cn(
        'flex w-full items-start gap-2 rounded px-2.5 py-1.5 text-start transition-colors',
        chon ? 'bg-primary-50' : 'hover:bg-subtle',
      )}
    >
      <span
        aria-hidden="true"
        className={cn('mt-1 size-1.5 shrink-0 rounded-full', chon ? 'bg-primary-600' : 'bg-primary-200')}
      />
      <span className="min-w-0">
        <span className={cn('block text-xs', chon && 'font-medium text-primary-700')}>{nhan}</span>
        <span className="block text-xs leading-snug text-content-secondary">{mo}</span>
      </span>
    </button>
  );
}

/**
 * Ba công tắc TRÌNH BÀY, gom trong nút `+` — chúng đổi cách viết ra một câu trả lời đã có. Dạng danh sách có DÒNG MÔ TẢ
 * chứ không phải dãy nút: "Khuyến nghị" hay "Luôn vẽ" không tự giải thích, và tooltip chỉ hiện cho người đã rê chuột.
 *
 * Độ dài là thanh NẤC chứ không kéo trơn theo số từ: cấu trúc lời văn rời rạc (đoạn, câu), mỗi nấc kéo theo một bộ trần
 * nhất quán ở máy chủ.
 */
export function TuyChonTraLoi({
  mucVan, doDai, bieuDo, onMucVan, onDoDai, onBieuDo,
}: {
  mucVan: string;
  doDai: string;
  bieuDo: string;
  onMucVan: (v: string) => void;
  onDoDai: (v: string) => void;
  onBieuDo: (v: string) => void;
}) {
  const { t } = useT();
  const doDaiDung = chuanHoa(DO_DAI, doDai, 'vua');
  return (
    <div className="w-full divide-y divide-default">
      <Nhom ten={t('troLyAiHoiTuyChon.tangTraLoi')} ghi={t('troLyAiHoiTuyChon.tangTraLoiGhi')}>
        {CAP_VAN.map((ma) => (
          <Muc
            key={ma}
            nhan={t(`troLyAiHoiCapVan.${ma}.nhan`)}
            mo={t(`troLyAiHoiCapVan.${ma}.mo`)}
            chon={mucVan === ma}
            onChon={() => onMucVan(ma)}
          />
        ))}
      </Nhom>
      <Nhom ten={t('troLyAiHoiTuyChon.doDai')} ghi={t('troLyAiHoiTuyChon.doDaiGhi')}>
        <div className="px-3.5 pb-1 pt-2">
          <Slider
            min={0}
            max={DO_DAI.length - 1}
            step={1}
            value={DO_DAI.indexOf(doDaiDung)}
            onChange={(i) => onDoDai(DO_DAI[i] ?? 'vua')}
            aria-label={t('troLyAiHoiTuyChon.doDai')}
            // Nhãn dưới thanh thay cho tooltip — tooltip chỉ hiện cho người đã đoán được nó là gì.
            marks={Object.fromEntries(
              DO_DAI.map((ma, i) => [
                i,
                <span key={ma} className="whitespace-nowrap text-2xs">
                  {t(`troLyAiHoiDoDai.${ma}.nhan`)}
                </span>,
              ]),
            )}
            tooltip={{ open: false }}
          />
          <p className="mt-5 text-xs leading-snug text-content-secondary">{t(`troLyAiHoiDoDai.${doDaiDung}.mo`)}</p>
        </div>
      </Nhom>
      <Nhom ten={t('troLyAiHoiTuyChon.bieuDo')}>
        {CHON_BIEU_DO.map((ma) => (
          <Muc
            key={ma}
            nhan={t(`troLyAiHoiChonBieuDo.${ma}.nhan`)}
            mo={t(`troLyAiHoiChonBieuDo.${ma}.mo`)}
            chon={bieuDo === ma}
            onChon={() => onBieuDo(ma)}
          />
        ))}
      </Nhom>
    </div>
  );
}

/**
 * Công tắc tra web BA BẬC, đặt ngay trong khối nhập chứ không giấu trong menu: nó đổi chỗ hệ thống ĐI TÌM (tức đổi
 * chính câu trả lời) và đổi độ trễ tới 6–7 giây — phải thấy được trước khi bấm gửi.
 *
 * Nút CHU KỲ chứ không phải ba nút: khung nhập đã có `+`, mic và gửi; nhãn hiện luôn bậc đang chọn.
 */
export function NutTraWeb({ web, onWeb, disabled = false }: { web: string; onWeb: (v: BacWeb) => void; disabled?: boolean }) {
  const { t } = useT();
  const bac = chuanHoa(BAC_WEB, web, 'khong');
  const i = BAC_WEB.indexOf(bac);
  const tat = bac === 'khong';
  return (
    <Tooltip title={t(`troLyAiHoiChonWeb.${bac}.mo`)}>
      <Button
        size="sm"
        variant={bac === 'chi_web' ? 'primary' : tat ? 'secondary' : 'outline'}
        aria-label={t('troLyAiHoiTuyChon.traWebNhan', { bac: t(`troLyAiHoiChonWeb.${bac}.nhan`) })}
        disabled={disabled}
        className="shrink-0"
        icon={<GlobalOutlined />}
        onClick={() => onWeb(BAC_WEB[(i + 1) % BAC_WEB.length] ?? 'khong')}
      >
        {tat ? t('troLyAiHoiTuyChon.traWebNgan') : t(`troLyAiHoiChonWeb.${bac}.nhan`)}
      </Button>
    </Tooltip>
  );
}
